import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { SERVICES } from "@/data/content";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/40";

export default function ContactForm({ source = "contact" }) {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const service = searchParams.get("service");
    const message = searchParams.get("message");
    if (service || message) {
      setForm((f) => ({ ...f, service: service || f.service, message: message || f.message }));
    }
  }, [searchParams]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post("/enquiries", { ...form, source });
      toast.success("Enquiry sent", {
        description: res.data?.email_sent
          ? "Check your inbox — we've emailed you a confirmation."
          : "We'll get back to you within one business day.",
      });
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry", { description: "Please try again or email hello@techaxisltd.co.uk." });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="cf-name">Name *</label>
          <input id="cf-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Jane Smith" className={inputCls} data-testid="contact-name-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="cf-email">Work email *</label>
          <input id="cf-email" required type="email" value={form.email} onChange={set("email")} placeholder="jane@company.co.uk" className={inputCls} data-testid="contact-email-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="cf-phone">Phone</label>
          <input id="cf-phone" value={form.phone} onChange={set("phone")} placeholder="+44 ..." className={inputCls} data-testid="contact-phone-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="cf-company">Company</label>
          <input id="cf-company" value={form.company} onChange={set("company")} placeholder="Company Ltd." className={inputCls} data-testid="contact-company-input" />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="cf-service">Service of interest</label>
          <select id="cf-service" value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`} data-testid="contact-service-select">
            <option value="" className="bg-navy-900">Select a service</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.name} className="bg-navy-900">{s.name}</option>
            ))}
            <option value="Hardware / Products" className="bg-navy-900">Hardware / Products</option>
            <option value="Other" className="bg-navy-900">Something else</option>
          </select>
        </div>
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400" htmlFor="cf-message">Tell us about your project *</label>
        <textarea id="cf-message" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder="What are you building, what problem does it solve, and when do you need it?" className={inputCls} data-testid="contact-message-input" />
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit-button"
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-electric px-6 py-3.5 text-sm font-semibold text-white shadow-[0_0_25px_rgba(0,102,255,0.35)] transition-all hover:bg-cyan-400 hover:text-ink disabled:opacity-60 sm:w-auto"
      >
        {sending ? "Sending..." : "Send enquiry"}
        <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
      <p className="text-xs text-slate-500">We reply within one business day. Your details are never shared.</p>
    </form>
  );
}
